import React, { useState, useEffect } from "react"
import { graphql, navigate } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import PropTypes, { arrayOf } from "prop-types"
import { v4 as uuidv4 } from "uuid"

import ColorTheme from "../../ui/ColorTheme/ColorTheme"
import Header from "../../ui/Header/Header"
import SocialShare from "../../ui/SocialShare/SocialShare"
import Container from "../../ui/Container/Container"
import NextResult from "../../ui/NextResult/NextResult"
import styles from "./QuizResult.module.scss"

import placeholder from "../../images/bingeplaceholder_noimage-v1.png"
import { QuizSEO } from "../../ui/QuizSEO"
import { useQuizData } from "../../utils/querys/useQuizData"
import { sendGTMEvent } from "../../utils/googleAnalytics"

const QuizResult = ({ location, pageContext, data }) => {
    const {
        result,
        resultText,
        resultImg,
        resultRating,
        episodeLink,
        castLink,
        reviewLink,
    } = pageContext

    const image = getImage(resultImg?.node)

    const staticData = useQuizData() || {}
    const otherMeta = data.allTestQuiz1Csv.edges[0].node.meta
    const {
        meta: {
            nodes: [{ meta: staticMeta }],
        },
    } = staticData

    const metadata = { ...staticMeta, ...otherMeta }

    const [reTakeIndex, setReTakeIndex] = useState(1)

    useEffect(() => {
        sendGTMEvent("widget complete", `Result = ${result}`)
    }, [])

    useEffect(() => {
        if (location.state && typeof window == "object") {
            setReTakeIndex(uuidv4())
        }
    }, [location])

    const handleShareText = () => {
        const shareText = metadata.shareTextResult
        return shareText?.replace("XXX", result)
    }

    return (
        <Container>
            <QuizSEO meta={metadata} />

            <ColorTheme
                lighterColor={metadata?.styles?.lighterColor}
                darkerColor={metadata?.styles?.darkerColor}
            />
            <Header
                siteTitle={metadata.name}
                border
                shareText={handleShareText()}
                shareUrl={
                    typeof window == "object"
                        ? `${window.location.origin}/what-should-i-binge-tv-quiz/`
                        : ""
                }
            />
            <div className={styles.container}>
                <div className={styles.resultInformationContainer}>
                    <div className={styles.resultImageContainer}>
                        {image ? (
                            <>
                                <GatsbyImage
                                    image={image}
                                    alt={`Result: ${result}`}
                                    imgClassName={styles.resultImage}
                                />

                                <p className={styles.resultRating}>
                                    {resultRating}
                                </p>
                            </>
                        ) : (
                            <img src={placeholder} alt={`Result: ${result}`} />
                        )}
                    </div>

                    <div className={styles.resultTextContainer}>
                        <h1 className={styles.resultTitle}>{result}</h1>
                        <p>{resultText}</p>

                        <div className={styles.linksContainer}>
                            <a
                                className={styles.links}
                                href={episodeLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() =>
                                    sendGTMEvent(
                                        "widget complete click",
                                        `episode list`
                                    )
                                }
                            >
                                Episode List
                            </a>
                            <a
                                className={styles.links}
                                href={castLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() =>
                                    sendGTMEvent(
                                        "widget complete click",
                                        `cast and crew`
                                    )
                                }
                            >
                                Cast and Crew
                            </a>
                            <a
                                className={styles.links}
                                href={reviewLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() =>
                                    sendGTMEvent(
                                        "widget complete click",
                                        `user reviews`
                                    )
                                }
                            >
                                User Reviews
                            </a>

                            <NextResult
                                className={`${styles.darkButton} ${styles.nextButton}`}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.border} />
                <div className={styles.bottomContainer}>
                    <button
                        onClick={() => {
                            sendGTMEvent("widget complete click", `retake quiz`)

                            navigate("/what-should-i-binge-tv-quiz", {
                                state: {
                                    startQuizFromResults: true,
                                    submitFromResults: false,
                                    reTakeIndex: reTakeIndex,
                                },
                            })
                        }}
                        className={styles.retakeButton}
                    >
                        Re-take Quiz
                    </button>
                    <p style={{ textAlign: "center" }}>
                        Share the quiz and your results with your friends!
                    </p>
                    <SocialShare
                        shareText={handleShareText()}
                        shareUrl={
                            typeof window == "object"
                                ? `${window.location.origin}/what-should-i-binge-tv-quiz/`
                                : ""
                        }
                        className={styles.socialShareContainer}
                    />

                    <div className={styles.inverseTitleContainer}>
                        <h2 className={styles.inverseTitle}>{metadata.name}</h2>
                    </div>

                    <div className={styles.creditsContainer}>
                        <h4 className={styles.textColor}>editorial</h4>
                        <p className={styles.creditTitles}>
                            {metadata.editorial}
                        </p>
                        <h4 className={styles.textColor}>UX/UI</h4>
                        <p className={styles.creditTitles}>{metadata.ui}</p>
                        <h4 className={styles.textColor}>development</h4>
                        <p className={styles.creditTitles}>
                            {metadata.developer}
                        </p>
                        <h4 className={styles.textColor}>Design</h4>
                        <p className={styles.creditTitles}>{metadata.design}</p>
                    </div>
                </div>
            </div>
        </Container>
    )
}

QuizResult.propTypes = {
    location: PropTypes.shape({
        state: PropTypes.shape({
            resultList: PropTypes.arrayOf(
                PropTypes.shape({
                    fields: PropTypes.shape({
                        result_slug: PropTypes.string.isRequired,
                    }).isRequired,
                    results: PropTypes.shape({
                        result: PropTypes.string.isRequired,
                    }).isRequired,
                    score: PropTypes.number.isRequired,
                })
            ).isRequired,
            reTakeIndex: PropTypes.string,
            key: PropTypes.string.isRequired,
        }),
    }).isRequired,
    pageContext: PropTypes.shape({
        castLink: PropTypes.string,
        episodeLink: PropTypes.string,
        reviewLink: PropTypes.string,
        result: PropTypes.string.isRequired,
        //  resultImg: PropTypes.string,
        resultText: PropTypes.string.isRequired,
        resultRating: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
    }),
    data: PropTypes.shape({
        allTestQuiz1Csv: PropTypes.shape({
            edges: arrayOf(
                PropTypes.shape({
                    node: PropTypes.shape({
                        meta: PropTypes.shape({
                            design: PropTypes.string.isRequired,
                            developer: PropTypes.string.isRequired,
                            editorial: PropTypes.string.isRequired,
                            name: PropTypes.string.isRequired,
                            shareTextResult: PropTypes.string.isRequired,
                            styles: PropTypes.shape({
                                darkerColor: PropTypes.string.isRequired,
                                lighterColor: PropTypes.string.isRequired,
                            }).isRequired,
                        }).isRequired,
                    }).isRequired,
                }).isRequired
            ).isRequired,
        }).isRequired,
    }).isRequired,
}

export const query = graphql`
    query MyQuery {
        allTestQuiz1Csv(filter: { meta: { name: { ne: null } } }) {
            edges {
                node {
                    meta {
                        name
                        styles {
                            darkerColor
                            lighterColor
                        }
                        shareTextResult
                        developer
                        design
                        editorial
                        ui
                    }
                }
            }
        }
    }
`

export default QuizResult
